import { Directive, ElementRef, HostListener, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appNumberFormat]'
})
export class NumberFormatDirective implements OnInit {
  @Input() decimalPlaces: number = 2; // Default 2 decimal places
  @Input() maxDigitsBeforeDecimal: number = 16; // Default 16 digits before decimal (for decimal(18,2))
  @Input() numberOnly: boolean = true; // Default to number only
  @Input() thousandSeparator: string = ',';
  @Input() decimalSeparator: string = '.';
  @Output() valueChange = new EventEmitter<number>();

  private regex!: RegExp;
  private specialKeys: Array<string> = ['Backspace', 'Tab', 'End', 'Home', 'Delete', 'ArrowLeft', 'ArrowRight'];

  constructor(
    private el: ElementRef,
    private ngControl: NgControl
  ) {}

  ngOnInit() {
    // Create regex pattern based on decimal places and max digits
    const decimalPattern = this.decimalPlaces > 0 ? `(\\${this.decimalSeparator}\\d{0,${this.decimalPlaces}})?` : '';
    this.regex = new RegExp(`^\\d{1,${this.maxDigitsBeforeDecimal}}(${this.thousandSeparator}\\d{3})*${decimalPattern}$`);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Allow special keys
    if (this.specialKeys.indexOf(event.key) !== -1) {
      return;
    }

    // If numberOnly is false, allow all characters
    if (!this.numberOnly) {
      return;
    }

    const current: string = this.el.nativeElement.value;
    const position = this.el.nativeElement.selectionStart;
    
    // Check if it's a number or decimal separator
    if (event.key !== this.decimalSeparator && (event.key < '0' || event.key > '9')) {
      event.preventDefault();
      return;
    }

    // Prevent multiple decimal separators
    if (event.key === this.decimalSeparator && current.includes(this.decimalSeparator)) {
      event.preventDefault();
      return;
    }

    // Prevent decimal separator if decimalPlaces is 0
    if (this.decimalPlaces === 0 && event.key === this.decimalSeparator) {
      event.preventDefault();
      return;
    }

    // Check maximum digits before decimal
    if (event.key >= '0' && event.key <= '9') {
      const decimalIndex = current.indexOf(this.decimalSeparator);
      const isBeforeDecimal = decimalIndex === -1 || position <= decimalIndex;
      
      if (isBeforeDecimal) {
        // Count current digits before decimal (excluding thousand separators)
        const beforeDecimalPart = decimalIndex === -1 ? current : current.substring(0, decimalIndex);
        const digitsBeforeDecimal = beforeDecimalPart.replace(new RegExp(this.thousandSeparator, 'g'), '').length;
        
        // Check if adding this digit would exceed the limit
        // Allow if we're replacing selected text
        const hasSelection = this.el.nativeElement.selectionStart !== this.el.nativeElement.selectionEnd;
        if (digitsBeforeDecimal >= this.maxDigitsBeforeDecimal && !hasSelection) {
          event.preventDefault();
          return;
        }
      }
    }

    // Prevent typing more than allowed decimal places
    if (current.includes(this.decimalSeparator)) {
      const parts = current.split(this.decimalSeparator);
      const decimals = parts[1] || '';
      
      // If we're typing after the decimal and reached max decimal places
      if (position > current.indexOf(this.decimalSeparator) && decimals.length >= this.decimalPlaces) {
        // Allow if we're replacing selected text
        if (this.el.nativeElement.selectionStart !== this.el.nativeElement.selectionEnd) {
          return;
        }
        event.preventDefault();
        return;
      }
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    if (!this.numberOnly) {
      return;
    }

    event.preventDefault();
    const pastedInput: string = event.clipboardData?.getData('text/plain') || '';
    
    // Clean the pasted input
    let cleanedInput = pastedInput.replace(/[^\d.]/g, '');
    
    // Handle decimal places
    const parts = cleanedInput.split('.');
    if (parts.length > 2) {
      cleanedInput = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit digits before decimal
    if (parts[0].length > this.maxDigitsBeforeDecimal) {
      parts[0] = parts[0].substring(0, this.maxDigitsBeforeDecimal);
    }
    
    // Limit decimal places
    if (parts.length === 2 && parts[1].length > this.decimalPlaces) {
      parts[1] = parts[1].substring(0, this.decimalPlaces);
    }
    
    cleanedInput = parts.join('.');

    // Update the value
    this.updateValue(cleanedInput);
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Remove all non-numeric characters except decimal separator
    if (this.numberOnly) {
      value = value.replace(new RegExp(`[^\\d${this.decimalSeparator}]`, 'g'), '');
    }

    // Apply formatting with cursor position preservation
    this.updateValue(value, true);
  }

  @HostListener('blur')
  onBlur(): void {
    const value = this.el.nativeElement.value;
    
    // Remove thousand separators for processing
    const cleanValue = value.replace(new RegExp(this.thousandSeparator, 'g'), '');
    
    if (!cleanValue || isNaN(parseFloat(cleanValue))) {
      return;
    }

    // Format with proper decimal places
    const parts = cleanValue.split(this.decimalSeparator);
    let formattedValue = parts[0];

    if (this.decimalPlaces > 0) {
      const decimals = parts[1] || '';
      const paddedDecimals = decimals.padEnd(this.decimalPlaces, '0');
      formattedValue = `${parts[0]}${this.decimalSeparator}${paddedDecimals.substring(0, this.decimalPlaces)}`;
    }

    this.updateValue(formattedValue);
  }

  private updateValue(value: string, preserveCursor: boolean = false): void {
    const input = this.el.nativeElement;
    const originalValue = input.value;
    const cursorPosition = preserveCursor ? input.selectionStart : null;
    
    // Remove existing thousand separators
    let cleanValue = value.replace(new RegExp(this.thousandSeparator, 'g'), '');
    
    // Split by decimal
    const parts = cleanValue.split(this.decimalSeparator);
    
    // Limit digits before decimal
    if (parts[0].length > this.maxDigitsBeforeDecimal) {
      parts[0] = parts[0].substring(0, this.maxDigitsBeforeDecimal);
    }
    
    // Add thousand separators to integer part
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandSeparator);
    
    // Limit decimal places
    if (parts[1] && parts[1].length > this.decimalPlaces) {
      parts[1] = parts[1].substring(0, this.decimalPlaces);
    }
    
    // Join back
    const formattedValue = parts.join(this.decimalSeparator);
    
    // Update the input value
    input.value = formattedValue;
    
    // Update the form control
    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.setValue(formattedValue, { emitEvent: false });
    }
    
    // Restore cursor position
    if (preserveCursor && cursorPosition !== null) {
      const newCursorPosition = this.calculateCursorPosition(
        originalValue,
        formattedValue,
        cursorPosition
      );
      
      // Use setTimeout to ensure the cursor is set after the value update
      setTimeout(() => {
        input.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    }
    
    // Emit numeric value
    const numericValue = parseFloat(cleanValue) || 0;
    this.valueChange.emit(numericValue);
  }

  private calculateCursorPosition(oldValue: string, newValue: string, oldPosition: number): number {
    // If cursor was at the end, keep it at the end
    if (oldPosition >= oldValue.length) {
      return newValue.length;
    }

    // Count digits before cursor in old value
    let digitsBeforeCursor = 0;
    let foundDecimal = false;
    
    for (let i = 0; i < oldPosition && i < oldValue.length; i++) {
      const char = oldValue[i];
      if (char >= '0' && char <= '9') {
        digitsBeforeCursor++;
      } else if (char === this.decimalSeparator) {
        foundDecimal = true;
      }
    }

    // Find new cursor position based on digit count
    let newPosition = 0;
    let digitCount = 0;
    
    for (let i = 0; i < newValue.length; i++) {
      const char = newValue[i];
      
      if (char >= '0' && char <= '9') {
        digitCount++;
        if (digitCount === digitsBeforeCursor) {
          newPosition = i + 1;
          
          // If we're after the decimal point, make sure we account for it
          if (foundDecimal && newValue.indexOf(this.decimalSeparator) > -1 && 
              newValue.indexOf(this.decimalSeparator) <= i) {
            // Position is correct
          }
          break;
        }
      }
      
      // If we haven't reached our digit count yet, keep moving
      if (digitCount < digitsBeforeCursor) {
        newPosition = i + 1;
      }
    }

    // If we were after the decimal in the old value, ensure we're after it in the new value
    if (foundDecimal) {
      const decimalIndex = newValue.indexOf(this.decimalSeparator);
      if (decimalIndex > -1 && newPosition <= decimalIndex) {
        // We need to count digits after decimal
        const digitsAfterDecimalInOld = this.countDigitsAfterPosition(oldValue, oldValue.indexOf(this.decimalSeparator), oldPosition);
        if (digitsAfterDecimalInOld > 0) {
          newPosition = decimalIndex + 1 + Math.min(digitsAfterDecimalInOld, this.decimalPlaces);
        }
      }
    }

    return Math.min(newPosition, newValue.length);
  }

  private countDigitsAfterPosition(value: string, startPos: number, endPos: number): number {
    let count = 0;
    for (let i = startPos + 1; i < endPos && i < value.length; i++) {
      if (value[i] >= '0' && value[i] <= '9') {
        count++;
      }
    }
    return count;
  }

  // Helper method to get numeric value
  getNumericValue(): number {
    const value = this.el.nativeElement.value;
    const cleanValue = value.replace(new RegExp(this.thousandSeparator, 'g'), '');
    return parseFloat(cleanValue) || 0;
  }
}